import { type ComponentPropsWithoutRef, type DragEvent, type FocusEvent, memo, useCallback } from 'react';

import { cn } from '@air/utils-tailwind';

import { PanelContent } from '~/components/v2/components/shared/panel/PanelContent';
import { PushModeContainer } from '~/components/v2/components/workspace/workspace-sidebar/PushModeContainer';

export type WorkspaceSidebarContainerProps = {
  isPushMode?: boolean;
  isPushFocused: boolean;
  suppressHoverOpen?: boolean;
  onPushFocus: () => void;
  onPushBlur: (event: FocusEvent) => void;
  onNotificationsHover: () => void;
  onHoverZoneLeave?: () => void;
  onPushPointerLeave?: () => void;
} & Pick<ComponentPropsWithoutRef<'div'>, 'children'>;

export const WorkspaceSidebarContainer = memo(
  ({
    isPushMode,
    isPushFocused,
    suppressHoverOpen,
    onPushFocus,
    onPushBlur,
    onNotificationsHover,
    onHoverZoneLeave,
    onPushPointerLeave,
    children,
  }: WorkspaceSidebarContainerProps) => {
    const handleDragLeave = useCallback(
      (event: DragEvent<HTMLElement>) => {
        if (event.relatedTarget instanceof Node && !event.currentTarget.contains(event.relatedTarget)) {
          onPushPointerLeave?.();
        }
      },
      [onPushPointerLeave],
    );

    if (isPushMode) {
      return (
        <PushModeContainer
          suppressHoverOpen={suppressHoverOpen}
          onNotificationsHover={onNotificationsHover}
          onHoverZoneLeave={onHoverZoneLeave}
          onPushPointerLeave={onPushPointerLeave}
          onDragLeave={handleDragLeave}
          onPushFocus={onPushFocus}
          onPushBlur={onPushBlur}
        >
          {children}
        </PushModeContainer>
      );
    }

    return (
      <PanelContent
        className={cn(
          'animation-duration-200 animation-width-[var(--sidebar-width)] relative z-40 flex w-[var(--sidebar-width)] shrink-0 overflow-hidden data-[state=closed]:animate-width-out',
          !isPushFocused && 'data-[state=open]:animate-width-in',
        )}
        onPointerEnter={onNotificationsHover}
      >
        <div className="flex min-h-0 grow flex-col">{children}</div>
      </PanelContent>
    );
  },
);

WorkspaceSidebarContainer.displayName = 'WorkspaceSidebarContainer';
